import { useState, useCallback, useRef, useEffect } from 'react';
import { Loader2, ChevronDown } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLegalDocumentContent } from '@/hooks/useLegalDocumentContent';
import { usePostMeAgreementsAccept, getGetMeAgreementsPendingQueryKey } from '@/api/auth/self/self';
import { PendingAgreement } from '@/api/auth/missionImpactAuthAPI.schemas';
import { useLanguage } from '@/contexts/LanguageContext';
import { logger } from '@/services/logger';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

interface AgreementStepProps {
  /** List of pending agreements that need acceptance */
  pendingAgreements: PendingAgreement[];
  /** Called when all agreements have been accepted */
  onAllAccepted: () => void;
  /** Called when user wants to go back (optional - hides back button if not provided) */
  onBack?: () => void;
}

/**
 * Step for accepting agreements during onboarding.
 * If no pending agreements, shows a message and allows proceeding.
 */
export function AgreementStep({ pendingAgreements, onAllAccepted, onBack }: AgreementStepProps) {
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAccepting, setIsAccepting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // If no agreements pending, show skip message
  if (pendingAgreements.length === 0) {
    return (
      <Card className="h-[480px] flex flex-col">
        <CardHeader>
          <CardTitle>{t.userOnboardingWizard.agreementStep.title}</CardTitle>
          <CardDescription>{t.userOnboardingWizard.agreementStep.description}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <p className="text-muted-foreground">{t.userOnboardingWizard.agreementStep.noAgreementsRequired}</p>
        </CardContent>
        <CardFooter className={`flex pt-4 ${onBack ? 'justify-between' : 'justify-end'}`}>
          {onBack && (
            <Button type="button" variant="outline" onClick={onBack}>
              {t.userOnboardingWizard.agreementStep.back}
            </Button>
          )}
          <Button type="button" onClick={onAllAccepted}>
            {t.userOnboardingWizard.themeStep.continue}
          </Button>
        </CardFooter>
      </Card>
    );
  }

  // Get the current agreement to display
  const currentAgreement = pendingAgreements[currentIndex];
  const totalAgreements = pendingAgreements.length;
  const isLastAgreement = currentIndex === totalAgreements - 1;

  // Fetch agreement content for the current agreement
  const {
    content: agreementContent,
    isLoading: isLoadingContent,
    error: contentError,
  } = useLegalDocumentContent(
    currentAgreement.documentType ?? '',
    currentAgreement.version ?? '',
    language,
    {
      enabled: !!currentAgreement.documentType && !!currentAgreement.version,
    }
  );

  // Mutation to accept the agreement
  const { mutateAsync: acceptAgreement } = usePostMeAgreementsAccept();

  /**
   * Check if user has scrolled to the bottom of the content
   */
  const checkScrollPosition = useCallback(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport) {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      // Consider "at bottom" if within 20px of the end
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      if (isAtBottom) {
        setHasScrolledToBottom(true);
      }
    }
  }, []);

  /**
   * Reset scroll state when agreement content changes
   */
  useEffect(() => {
    setHasScrolledToBottom(false);
  }, [currentIndex, agreementContent]);

  /**
   * Set up scroll listener when content is loaded
   */
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport && agreementContent) {
      // Check initial position (content might be short enough to not need scrolling)
      const { scrollHeight, clientHeight } = viewport;
      if (scrollHeight <= clientHeight) {
        setHasScrolledToBottom(true);
      }

      viewport.addEventListener('scroll', checkScrollPosition);
      return () => { viewport.removeEventListener('scroll', checkScrollPosition); };
    }
  }, [agreementContent, checkScrollPosition]);

  /**
   * Handle agreement acceptance
   */
  const handleAccept = useCallback(async () => {
    if (!currentAgreement.documentType || !currentAgreement.version) {
      return;
    }

    setIsAccepting(true);
    try {
      await acceptAgreement({
        data: {
          documentType: currentAgreement.documentType,
          version: currentAgreement.version,
        },
      });

      logger.info('User accepted agreement during onboarding', {
        context: 'AgreementStep.handleAccept',
        documentType: currentAgreement.documentType,
        version: currentAgreement.version,
        currentIndex,
        totalAgreements,
      });

      if (isLastAgreement) {
        // All agreements accepted - invalidate query and notify parent
        await queryClient.invalidateQueries({ queryKey: getGetMeAgreementsPendingQueryKey() });
        onAllAccepted();
      } else {
        // Move to next agreement
        setCurrentIndex((prev) => prev + 1);
      }
    } catch (error) {
      logger.error(error instanceof Error ? error : new Error('Failed to accept agreement'), {
        context: 'AgreementStep.handleAccept',
        documentType: currentAgreement.documentType,
      });
      toast.error(t.userOnboardingWizard.agreementStep.acceptError);
    } finally {
      setIsAccepting(false);
    }
  }, [acceptAgreement, currentAgreement, currentIndex, isLastAgreement, onAllAccepted, queryClient, t, totalAgreements]);

  // Show progress indicator if there are multiple agreements
  const progressText = totalAgreements > 1 ? `(${String(currentIndex + 1)}/${String(totalAgreements)})` : '';

  // Get document title based on type
  const getDocumentTitle = () => {
    if (isLoadingContent) return t.userOnboardingWizard.agreementStep.loading;
    if (currentAgreement.documentType === 'privacy-policy') {
      return t.userOnboardingWizard.agreementStep.privacyPolicyTitle;
    }
    if (currentAgreement.documentType === 'terms-of-use') {
      return t.userOnboardingWizard.agreementStep.termsOfUseTitle;
    }
    return t.userOnboardingWizard.agreementStep.title;
  };

  return (
    <Card className="h-[480px] flex flex-col">
      <CardHeader>
        <CardTitle>
          {`${getDocumentTitle()} ${progressText}`.trim()}
        </CardTitle>
        <CardDescription>{t.userOnboardingWizard.agreementStep.description}</CardDescription>
      </CardHeader>

      <CardContent className="flex-1">
        {isLoadingContent ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : contentError ? (
          <div className="py-8 text-center text-destructive">
            {t.userOnboardingWizard.agreementStep.loadError}
          </div>
        ) : (
          <div className="relative">
            <ScrollArea ref={scrollAreaRef} className="h-[300px] rounded-md border p-4">
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(agreementContent ?? '') }}
              />
            </ScrollArea>
            {!hasScrolledToBottom && (
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center pb-2 pt-8 bg-gradient-to-t from-background to-transparent pointer-events-none">
                <span className="text-xs text-muted-foreground">{t.userOnboardingWizard.agreementStep.scrollToAccept}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground animate-bounce" />
              </div>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className={`flex pt-4 ${onBack ? 'justify-between' : 'justify-end'}`}>
        {onBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isAccepting}
          >
            {t.userOnboardingWizard.agreementStep.back}
          </Button>
        )}
        <Button
          type="button"
          onClick={() => { void handleAccept(); }}
          disabled={isAccepting || isLoadingContent || !!contentError || !hasScrolledToBottom}
        >
          {isAccepting
            ? t.userOnboardingWizard.agreementStep.accepting
            : t.userOnboardingWizard.agreementStep.accept}
        </Button>
      </CardFooter>
    </Card>
  );
}
